import React, { useEffect, useState } from "react";
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Link, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography,
} from "@mui/material";
import * as svc from "../services/facturacionService";

const money = (v) => Number(v || 0).toLocaleString("es-BO", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fecha = (v) => String(v || "").slice(0, 10);

// Todos los pagos (de cualquier factura) que salieron de un mismo abono del extracto bancario, identificado por el extracto (vinculo) o, en pagos manuales, por el código de banco.
export default function PagosRelacionadosDialog({ open, filtro, pagoActual, onClose }) {
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open || !filtro) return undefined;
    let vigente = true;
    setDatos(null); setError(""); setCargando(true);
    svc.getPagosRelacionados(filtro)
      .then((res) => { if (vigente) setDatos(res?.data?.data ?? res?.data); })
      .catch((e) => { if (vigente) setError(e?.response?.data?.message || "No se pudieron cargar los pagos relacionados."); })
      .finally(() => { if (vigente) setCargando(false); });
    return () => { vigente = false; };
  }, [open, filtro]);

  const ext = datos?.extracto;
  const abono = Number(ext?.credito) || 0;
  const diferencia = ext ? Number((abono - (datos?.suma || 0)).toFixed(2)) : 0;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" scroll="paper">
      <DialogTitle sx={{ fontSize: 16, fontWeight: 800, py: 1.25 }}>
        Pagos del código {ext?.cod_transaccion || filtro?.cod_banco || ""}
        <Typography component="div" sx={{ fontSize: 12, color: "text.secondary", fontWeight: 400 }}>
          {datos?.por === "extracto" ? "Pagos aplicados al mismo abono del extracto bancario" : "Pagos manuales que comparten este código de banco"}
        </Typography>
      </DialogTitle>
      <DialogContent dividers sx={{ bgcolor: "background.default", p: 1.5, display: "grid", gap: 1 }}>
        {cargando && <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}><CircularProgress size={24} /></Box>}
        {error && <Alert severity="error" sx={{ py: 0 }}>{error}</Alert>}

        {datos && (
          <>
            {ext && (
              <Paper variant="outlined" sx={{ p: 1.25, display: "grid", gap: 0.5 }}>
                <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", alignItems: "center" }}>
                  <Chip size="small" label={`Extracto #${ext.id}`} />
                  <Chip size="small" label={fecha(ext.fecha)} />
                  {(ext.banco_nombre || ext.banco) && <Chip size="small" label={ext.banco_nombre || ext.banco} />}
                  <Chip size="small" color="primary" label={`Abono Bs ${money(abono)}`} sx={{ fontWeight: 700 }} />
                  <Chip size="small" variant="outlined" label={`Saldo sin aplicar Bs ${money(ext.saldo)}`} />
                </Box>
                <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{ext.descripcion || "-"}</Typography>
                {ext.adicional && <Typography sx={{ fontSize: 12, color: "text.secondary", overflowWrap: "anywhere" }}>{ext.adicional}</Typography>}
              </Paper>
            )}

            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 360 }}>
              <Table size="small" stickyHeader sx={{ "& td, & th": { py: 0.4, px: 0.9, fontSize: "0.78rem" } }}>
                <TableHead>
                  <TableRow sx={{ "& th": { fontWeight: 800 } }}>
                    <TableCell>Pago</TableCell>
                    <TableCell>Factura</TableCell>
                    <TableCell>Cliente</TableCell>
                    <TableCell>Fecha</TableCell>
                    <TableCell>Forma</TableCell>
                    <TableCell align="right">Monto Bs</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {datos.pagos.map((p) => (
                    <TableRow key={p.cod} selected={Number(p.cod) === Number(pagoActual)}>
                      <TableCell>#{p.cod}</TableCell>
                      <TableCell>
                        {p.id_servicio_cliente ? (
                          <Link href={`/reporte-cliente/${p.id_servicio_cliente}`} target="_blank" rel="noopener" underline="hover">N.º {p.num_fac || p.id_factura}</Link>
                        ) : `N.º ${p.num_fac || p.id_factura}`}
                        {p.estado_factura === "X" && <Chip size="small" color="error" variant="outlined" label="Anulada" sx={{ ml: 0.5, height: 18, fontSize: 10.5 }} />}
                      </TableCell>
                      <TableCell sx={{ maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={p.fac_Nombre}>{p.fac_Nombre || "-"}</TableCell>
                      <TableCell>{fecha(p.fecha_pago)}</TableCell>
                      <TableCell>{p.forma_pago || "-"}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>{money(p.monto)}</TableCell>
                    </TableRow>
                  ))}
                  {!datos.pagos.length && <TableRow><TableCell colSpan={6} sx={{ color: "text.secondary" }}>Sin pagos relacionados.</TableCell></TableRow>}
                </TableBody>
              </Table>
            </TableContainer>

            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
              <Typography sx={{ fontSize: 12.5, color: "text.secondary" }}>
                {datos.total} pago{datos.total === 1 ? "" : "s"}{datos.truncado ? ` (se muestran los ${datos.pagos.length} más recientes)` : ""}
              </Typography>
              <Typography sx={{ fontSize: 14, fontWeight: 800 }}>Total aplicado: Bs {money(datos.suma)}</Typography>
            </Box>
            {ext && Math.abs(diferencia) > 0.5 && (
              <Alert severity={diferencia > 0 ? "info" : "warning"} sx={{ py: 0 }}>
                {diferencia > 0
                  ? `Del abono de Bs ${money(abono)} aún no se aplicaron Bs ${money(diferencia)} a facturas.`
                  : `Los pagos suman Bs ${money(-diferencia)} más que el abono del extracto.`}
              </Alert>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: "none" }}>Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
}
